/**
 * Field Help — Recurring Charges
 *
 * Keys MUST match the field ids in
 * `features/recurring-charges/components/RecurringChargesConfig.ts`
 * (RECURRING_CHARGES_CONFIG.fields) so the "?" popover attaches to the right
 * column header / editor row / card field.
 *
 * Registry key is the HYPHENATED entity value ("recurring-charges") the config
 * carries (`entity:` in the Config), which is what `config.entity` passes to
 * FieldHelpPopover — NOT the underscore table name.
 *
 * A recurring charge bills a customer on a schedule against a specific
 * QuickBooks company (`realm_id`) — WTC or Schooley Mitchell.
 */

import type { EntityFieldHelp } from "./types";

export const recurringChargeFieldHelp: EntityFieldHelp = {
  realm_id: {
    title: "Billing Company (QuickBooks Realm)",
    description:
      "Which QuickBooks company this charge bills — West Tennessee Consulting or Schooley Mitchell. Shown as a colored badge.",
    usage:
      "WTC = our own consulting/MSP services; Schooley Mitchell = the cost-savings program. They are separate books — never mix.",
    relatedTo:
      "Maps to a connected QuickBooks realm. Friendly labels are managed in Admin → Settings → QuickBooks Realm Labels.",
  },
  customer_id: {
    title: "Customer",
    description: "The contact this charge bills each cycle.",
    relatedTo: "recurring charge → contact.",
  },
  schedule_type: {
    title: "Schedule",
    description: "How often the charge runs: daily, weekly, or monthly.",
    usage: "Drives when the next charge date advances after each run.",
  },
  next_charge_date: {
    title: "Next Charge Date",
    description: "The next date this charge is scheduled to run.",
    usage: "Advances automatically by the schedule after each successful charge.",
  },
  last_charged_at: {
    title: "Last Charged",
    description: "When this charge last ran successfully.",
    usage: "Read-only. Blank means it hasn't run yet.",
  },
  metadata: {
    title: "Charge Details",
    description: "Structured detail about the charge (amounts, line items, source), stored as JSON.",
    usage: "Read-only reference for what the charge covers.",
  },
  admin_email: {
    title: "Admin Email",
    description: "The admin whose QuickBooks connection owns this charge's realm.",
    usage: "Read-only. Identifies which connected account bills the charge.",
  },
  created_at: {
    title: "Created",
    description: "When this recurring charge was set up.",
  },
};
